import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "@/shared/BaseFragment";
import {Action, UserCache} from "@/shared/types";
import {getAuth} from "@firebase/auth";
import {User} from "@/shared/entities";
import {StyledEmpty} from "@/shared/StyledComponents";
import {PD_XLG, PD_XXXLG} from "@/shared/dimens";
import {AccountProfileContextTrait, Page, Pages} from "@/libwyze-shared/types";
import {BaseApp} from "@/shared/BaseApp";
import {Typography} from "@mui/material";
import React, {ReactElement} from "react";
import {ProviderQuickOnboardingFragment} from "@/libwyze-shared/components/ProviderQuickOnboardingFragment";

function getPracticePageNameTitleSuffix(): string {
  const practicePageId = BaseApp.CONTEXT.getTrait<AccountProfileContextTrait>().getPracticePageId();
  if (practicePageId) {
    const name = Pages.getInstance().getListItem(practicePageId)?.practicePageProfile?.basicInfo.name;
    return name ? ` - ${name}` : "";
  }
  return "";
}

export type BaseMainProps = BaseFragmentProps & {}

export type BaseMainState = BaseFragmentState & {
  user?: User,
  practicePage?: Page,
}

export abstract class BaseMain<P extends BaseMainProps = BaseMainProps, S extends BaseMainState = BaseMainState> extends BaseFragment<P, S> {

  protected readonly auth = getAuth();

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const user = await UserCache.getInstance().getUser(this.auth.currentUser.uid);
    const practicePageId = BaseApp.CONTEXT.getTrait<AccountProfileContextTrait>().getPracticePageId();
    if (practicePageId) {
      const practicePage = await Pages.getInstance().getOrLoadItem(practicePageId);
      this.setState({
        user,
        practicePage,
      });
    }
  }

  protected renderContainerContent(): React.ReactElement | null {
    if (!this.state.practicePage) {
      const redirectToSetup = false;
      return redirectToSetup
        ? <StyledEmpty emptyConfig={{
          title: "Almost there. You need to set up your profile",
          text: "You must complete the set up to view this page. Sign in to the myCare app to create your profile and link your practice.",
          iconUrl: "/images/complete-setup.svg",
          action: new Action("Go to myCare app", () => window.open("https://dentwyze.in/mycare", "_blank")),
        }}/>
        : <ProviderQuickOnboardingFragment path={this.props.path}/>;
    }
    const showTitleBar = true;
    const titleBarHeight = 40;
    const appConfig = BaseApp.CONTEXT.getAppConfig();
    const theme = appConfig.theme;
    return <div
      style={{display: "flex", flexDirection: "column", width: "100dvw", height: "100dvh", overflow: "hidden"}}>
      {showTitleBar
        ? <div
          className={"DragBar"}
          style={{
            position: "fixed",
            display: "flex",
            alignItems: "center",
            paddingLeft: PD_XLG,
            paddingRight: PD_XLG,
            width: "100%",
            height: titleBarHeight,
            flexShrink: 0,
            flexGrow: 0,
            gap: 24,
            background: theme.palette.primary.main,
            color: this.theme.palette.primary.contrastText,
            zIndex: 1000,
          }}>
          <Typography style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            paddingLeft: PD_XXXLG,
            paddingRight: PD_XXXLG,
            fontWeight: "bold", fontSize: "100%", width: "100%",
            textAlign: "center"
          }}>
            {`${appConfig.name + getPracticePageNameTitleSuffix()}`}
          </Typography>
        </div>
        : null}
      <div
        style={{
          position: "relative",
          marginTop: titleBarHeight,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          overflow: "hidden"
        }}>
        {this.renderContent()}
      </div>
    </div>;
  }

  protected abstract renderContent(): ReactElement;
}
